/* eslint-disable no-undef */
import { extractHostname } from "/Assets/js/common/utilities.js";
import utmTracking from "./utm-tracking.js";
import { valueMappings } from "./dataverse-field-mappings.js";

// Build O(1) lookup sets of the Dataverse-accepted keys. Anything we emit MUST be a
// member of these sets; otherwise form-capture-builder falls through to sending the
// raw string to Dataverse, which silently fails (option not found in choice list).
const VALID_LS = new Set(Object.keys(valueMappings.ht_leadsource));
const VALID_LSD = new Set(Object.keys(valueMappings.ht_leadsourcedetail));

// Per-ls default lsd. Used when we have a confirmed ls but no valid lsd to pair it with.
// Every value below has been verified to exist in valueMappings.ht_leadsourcedetail.
const DEFAULT_LSD_BY_LS = {
    "email": "other email",
    "event": "other event",
    "referral": "other referral",
    "phone": "phone inquiry",
    "print mail": "print mail",
    "website": "website-other",
    // ls values with no obvious detail partner fall back to "other".
    "display": "other",
    "organic search": "other",
    "paid search": "other",
    "paid social": "other",
    "paid video": "other",
    "print advertising": "other",
    "site visit": "other",
    "social": "other",
    "technical service": "other",
    "video": "other",
    "other": "other"
};

const FALLBACK_PAIR = { ls: "website", lsd: "website-other" };

// utm_medium spellings that all mean paid search. Ad platforms are inconsistent
// (Google/Bing Ads emit "cpc"/"ppc", manual tags use "paid search"/"paid_search"/"paidsearch"),
// and "paid+search"/"paid%20search" both decode to "paid search". Everything here maps to the
// canonical Dataverse ht_leadsource key "paid search".
const PAID_SEARCH_MEDIUMS = new Set([
    "paid search",
    "paidsearch",
    "cpc",
    "ppc"
]);

export default class LeadSource {
    constructor() { }

    // Public contract: always returns an array containing exactly one { ls, lsd } object.
    // Both ls and lsd are guaranteed to be non-empty AND to be valid keys in the
    // corresponding Dataverse valueMappings table.
    getLeadSourceValues() {
        const isUtmInUrl = utmTracking.utmPresentInUrl();
        const utmMedium = this._normalizeUtmValue(
            isUtmInUrl ? utmTracking.getParameterByName("utm_medium") : utmTracking.utmMedium()
        );
        const utmSource = this._normalizeUtmValue(
            isUtmInUrl ? utmTracking.getParameterByName("utm_source") : utmTracking.utmSource()
        );

        let result;
        if (utmMedium !== "" || utmSource !== "") {
            result = this._fromUtm(utmMedium, utmSource);
        } else {
            result = this._fromReferrer();
        }

        return [this._validatePair(result)];
    }

    _fromUtm(utmMedium, utmSource) {
        let ls = this._normalizeMedium(utmMedium);
        let lsd = utmSource;
        // Derive the missing partner so the pair is never asymmetric.
        if (ls === "" && lsd !== "") {
            ls = this._determineLeadSource(lsd).ls;
        } else if (lsd === "" && ls !== "") {
            lsd = DEFAULT_LSD_BY_LS[ls.toLowerCase()] || "";
        }
        return { ls, lsd };
    }

    // Canonicalizes a raw utm_medium to a Dataverse ht_leadsource key. Ad platforms emit many
    // spellings for paid search (cpc, ppc, paidsearch, paid_search, "paid+search"/"paid%20search"),
    // all of which must map to "paid search". Any other medium is returned lowercased/trimmed so it
    // can match a valid ht_leadsource key directly (e.g. "email", "social").
    _normalizeMedium(utmMedium) {
        if (typeof utmMedium !== "string") return "";
        const collapsed = utmMedium.trim().toLowerCase().replace(/[_+]/g, " ").replace(/\s+/g, " ");
        if (collapsed === "") return "";
        if (PAID_SEARCH_MEDIUMS.has(collapsed)) return "paid search";
        return collapsed;
    }

    _fromReferrer() {
        // Tiered referrer lookup: prefer the initial cross-domain referrer (set once per
        // session window). If it's missing or sentinel "direct", fall back to the last
        // cross-domain referrer (which UtmTracking refreshes whenever the user re-enters
        // from another site). Only as a last resort consult document.referrer, which is
        // unreliable across cross-domain navigations in modern browsers.
        let referrer = this._firstUsableReferrer([
            utmTracking.initialReferrer(),
            utmTracking.lastReferrer(),
            document.referrer
        ]);

        // No usable referrer of any kind → genuine direct visit.
        if (!referrer) {
            return { ...FALLBACK_PAIR };
        }

        const hostname = extractHostname(referrer).toLowerCase();
        if (!hostname) {
            return { ...FALLBACK_PAIR };
        }

        return this._determineLeadSource(hostname);
    }

    _firstUsableReferrer(candidates) {
        for (const candidate of candidates) {
            if (typeof candidate !== "string") continue;
            const trimmed = candidate.trim();
            if (trimmed === "" || trimmed === "direct") continue;
            return trimmed;
        }
        return "";
    }

    // Safely decode a raw UTM value (cookie or URL param). Returns a trimmed string,
    // or "" if the value is missing, blank, or undecodable. Never throws.
    _normalizeUtmValue(raw) {
        if (raw === null || raw === undefined) return "";
        let value = String(raw);
        try {
            value = decodeURIComponent(value.replace(/\+/g, " "));
        } catch {
            // Malformed sequence — keep the raw form rather than throwing.
        }
        return value.trim();
    }

    // Final safety net: guarantees both fields are non-empty AND members of the
    // Dataverse valueMappings tables. Anything not in the tables would cause a
    // silent submission failure, so it's coerced to a known-valid fallback here.
    _validatePair(pair) {
        const rawLs = pair && typeof pair.ls === "string" ? pair.ls.trim() : "";
        const rawLsd = pair && typeof pair.lsd === "string" ? pair.lsd.trim() : "";

        const lsValid = VALID_LS.has(rawLs);
        const lsdValid = VALID_LSD.has(rawLsd);

        if (lsValid && lsdValid) return { ls: rawLs, lsd: rawLsd };

        if (lsValid && !lsdValid) {
            return { ls: rawLs, lsd: DEFAULT_LSD_BY_LS[rawLs.toLowerCase()] || FALLBACK_PAIR.lsd };
        }

        if (!lsValid && lsdValid) {
            // Defense in depth: if the raw ls still signals paid search (e.g. a legacy
            // multiply-escaped "paid%20search" cookie surviving the encoding fix), never
            // reverse-derive it into "organic search" from the source table.
            if (this._normalizeMedium(rawLs) === "paid search") {
                return { ls: "paid search", lsd: rawLsd };
            }
            // Reverse-derive ls from the valid lsd using the rule table.
            const derived = this._determineLeadSource(rawLsd).ls;
            return {
                ls: VALID_LS.has(derived) ? derived : FALLBACK_PAIR.ls,
                lsd: rawLsd
            };
        }

        return { ...FALLBACK_PAIR };
    }

    // Rule table: each entry maps one or more hostname patterns to a fixed ls/lsd output.
    // Dotted patterns (e.g. "chatgpt.com") match via exact or suffix comparison, so
    // "chatgpt.com" will never bleed into "t.co". Bare-word patterns (e.g. "google")
    // match any hostname segment, so "google.co.uk" resolves correctly without listing
    // every TLD. Rules are evaluated top-to-bottom; place more-specific dotted patterns
    // (e.g. "gemini.google.com") before their bare-word counterparts (e.g. "google").
    static get _SOURCE_RULES() {
        return [
            // AI platforms
            { patterns: ["chatgpt.com", "chat.openai.com", "openai.com"], ls: "referral", lsd: "chatgpt.com" },
            { patterns: ["gemini.google.com"], ls: "referral", lsd: "gemini" },
            { patterns: ["perplexity.ai"], ls: "referral", lsd: "perplexity" },
            { patterns: ["copilot.microsoft.com", "edgepilot", "edgeservices"], ls: "referral", lsd: "copilot" },
            // { patterns: ["claude.ai"],  ls: "referral", lsd: "claude" },
            // { patterns: ["grok.com"],   ls: "referral", lsd: "grok" },
            // { patterns: ["meta.ai"],    ls: "referral", lsd: "meta-ai" },
            // { patterns: ["you.com"],    ls: "referral", lsd: "you.com" },
            // Search engines
            { patterns: ["google"], ls: "organic search", lsd: "google" },
            { patterns: ["bing"], ls: "organic search", lsd: "bing" },
            { patterns: ["baidu"], ls: "organic search", lsd: "baidu" },
            { patterns: ["yahoo"], ls: "organic search", lsd: "yahoo" },
            { patterns: ["ask"], ls: "organic search", lsd: "ask" },
            { patterns: ["duckduckgo"], ls: "organic search", lsd: "duckduckgo" },
            { patterns: ["naver"], ls: "organic search", lsd: "naver" },
            { patterns: ["sogou"], ls: "organic search", lsd: "sogou" },
            // Social platforms — "twitter", "x.com", and "t.co" all resolve to "twitter"
            { patterns: ["facebook"], ls: "social", lsd: "facebook" },
            { patterns: ["youtube"], ls: "social", lsd: "youtube" },
            { patterns: ["linkedin"], ls: "social", lsd: "linkedIn" },
            { patterns: ["instagram"], ls: "social", lsd: "instagram" },
            { patterns: ["twitter", "x.com", "t.co"], ls: "social", lsd: "twitter" },
            { patterns: ["wechat"], ls: "social", lsd: "wechat" },
            { patterns: ["pinterest"], ls: "social", lsd: "pinterest" },
            // Hypertherm family
            { patterns: ["hyperthermassociates.com"], ls: "website", lsd: "hyperthermassociates.com" },
            { patterns: ["hypertherm.com"], ls: "website", lsd: "hypertherm.com" },
            { patterns: ["robotmaster.com"], ls: "website", lsd: "robotmaster.com" },
            { patterns: ["omax.com"], ls: "website", lsd: "omax.com" },
            { patterns: ["onfastspring.com"], ls: "referral", lsd: "fastspring" }
        ];
    }

    _determineLeadSource(hostname) {
        for (const rule of LeadSource._SOURCE_RULES) {
            if (rule.patterns.some(p => this._hostnameMatches(hostname, p))) {
                return { ls: rule.ls, lsd: rule.lsd };
            }
        }
        return { ls: "referral", lsd: "other referral" };
    }

    // Dotted patterns use exact or suffix matching (prevents "t.co" matching "chatgpt.com").
    // Bare-word patterns use segment matching (allows "google" to match "www.google.co.uk").
    _hostnameMatches(hostname, pattern) {
        if (hostname === pattern) return true;
        if (hostname.endsWith("." + pattern)) return true;
        if (!pattern.includes(".") && hostname.split(".").includes(pattern)) return true;
        return false;
    }
}
